// Fragment packs, viewer side: bytes in, library parts out, no parsing.
//
// The server format is documented in back/utils/fragmentPack.js; the
// decoding itself lives in packDecodeCore.js (shared with the worker). A pack
// is a small JSON table of contents followed by every vertex and index
// array back to back, each on a 4-byte boundary, so every attribute is a
// typed-array VIEW onto the downloaded buffer - nothing is copied, nothing
// goes through GLTFLoader, and no worker round trip is needed.
//
// What comes out is exactly what GLTFWorkerPool builds from a parsed GLB:
// { geometry, material, name } per fragment, with the same attribute set
// and a material made by the same constructor, so everything downstream
// (pools, batches, LOD, picking) cannot tell the difference.
import { BufferAttribute, BufferGeometry } from 'three';
import { MeshoptDecoder } from 'three/examples/jsm/libs/meshopt_decoder.module.js';
import GLTFWorkerPool from './GLTFWorkerPool';
import { decodePackArrays } from './packDecodeCore';

export { PACK_MAGIC } from './packDecodeCore';
export const PACK_VERSION = 1;
// Version 2: quantised positions, octahedral normals, meshopt-coded
// streams (back/utils/fragmentPack.encodePackV2). Decoded to exactly the
// arrays version 1 carries (packDecodeCore), so everything downstream is
// unchanged.
export const PACK_VERSION_2 = 2;

let _decoderReady = null;
/** Resolve once the meshopt decoder (WASM) is ready; call before decodePack on v2. */
export function packDecoderReady() {
  if (!_decoderReady) _decoderReady = MeshoptDecoder.ready.then(() => true);
  return _decoderReady;
}

const buildMaterial = (props) => GLTFWorkerPool.prototype._reconstructMaterial.call(null, props);

/**
 * Library parts from decoded pack arrays (packDecodeCore, here or in
 * public/packDecodeWorker.js): Map(hash -> { geometry, material, name }).
 */
export function partsFromArrays(decoded) {
  const out = new Map();
  for (const f of decoded.fragments) {
    const geometry = new BufferGeometry();
    for (const a of f.attrs) geometry.setAttribute(a.name, new BufferAttribute(a.array, a.itemSize, a.normalized));
    if (f.index) geometry.setIndex(new BufferAttribute(f.index, 1));
    out.set(f.h, { geometry, material: buildMaterial(decoded.materials[f.m]), name: f.n || '' });
  }
  return out;
}

/**
 * Decode a pack on this thread into Map(hash -> { geometry, material, name }).
 * `buffer` is an ArrayBuffer (a fetch response or an IDB entry).
 */
export function decodePack(buffer) {
  const decoded = decodePackArrays(buffer, MeshoptDecoder);
  return { level: decoded.level, parts: partsFromArrays(decoded) };
}

// Off switch without a deploy: ?packs=off, or localStorage bl_packs=off.
export function packsEnabled() {
  try {
    if (typeof window !== 'undefined') {
      if (window.__packs === false) return false;
      if (/[?&]packs=off\b/.test(window.location.search || '')) return false;
      if (typeof localStorage !== 'undefined' && localStorage.getItem('bl_packs') === 'off') return false;
    }
  } catch (e) { /* private mode: the default stands */ }
  return true;
}
